import React, { Component, useState, useEffect, useCallback, useRef, forwardRef } from "react";
import { NavLink, useLocation, matchPath } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import { CSSTransition } from "react-transition-group";
import { useTranslation } from "react-i18next";
import "../Styles/global.scss"

const isMenuItemActive = (item, pathname) => {
	if (!item.to) {
		return false;
	}
	if (item.excludeActivePaths?.some((p) => pathname.startsWith(p))) {
		return false;
	}
	return matchPath({ path: item.to, end: !!item.end }, pathname) != null;
};

const hasActiveChild = (items, pathname) => {
	if (!items) {
		return false;
	}
	return items.some(
		(item) =>
			isMenuItemActive(item, pathname) ||
			(item.items && hasActiveChild(item.items, pathname))
	);
};

const findExpandedIndex = (items, pathname) => {
	if (!items) {
		return null;
	}
	for (let i = 0; i < items.length; i++) {
		if (items[i].items && hasActiveChild(items[i].items, pathname)) {
			return i;
		}
	}
	return null;
};

const AppSubmenu = forwardRef((props, ref) => {
	const [state, setState] = useState({});
	const { t } = useTranslation("common");
	const location = useLocation();
	const submenuRefs = useRef({});

	const onMenuItemClick = (event, item, index) => {
		//avoid processing disabled items
		if (item.disabled) {
			event.preventDefault();
			return true;
		}

		if (props.root && props.onRootItemClick) {
			props.onRootItemClick({
				originalEvent: event,
				item: item,
			});
		}

		//execute command
		if (item.command) {
			item.command({ originalEvent: event, item: item });
		}

		if (index === state.activeIndex) setState({ activeIndex: null });
		else setState({ activeIndex: index });

		if (props.onMenuItemClick) {
			props.onMenuItemClick({
				originalEvent: event,
				item: item,
			});
		}
	};

	const isHorizontalOrSlim = useCallback(() => {
		return props.layoutMode === "horizontal" || props.layoutMode === "slim";
	}, [props.layoutMode]);

	useEffect(() => {
		if (isHorizontalOrSlim()) {
			setState({ activeIndex: null });
		}
	}, [isHorizontalOrSlim]);

	useEffect(() => {
		if (isHorizontalOrSlim()) {
			return;
		}
		const expandedIndex = findExpandedIndex(props.items, location.pathname);
		if (expandedIndex !== null) {
			setState({ activeIndex: expandedIndex });
		} else {
			setState({ activeIndex: null });
		}
		// Only sync expansion from route changes; props.items is recreated each render in App.js.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, isHorizontalOrSlim]);

	const onKeyDown = (event, item, index) => {
		if (event.key === "Enter") {
			onMenuItemClick(event, item, index);
		}
	};

	const onMenuItemMouseEnter = (index) => {
		if (props.root && props.menuActive && isHorizontalOrSlim()) {
			setState({ activeIndex: index });
		}
	};

	const getSubmenuRef = (index) => {
		if (!submenuRefs.current[index]) {
			submenuRefs.current[index] = React.createRef();
		}
		return submenuRefs.current[index];
	};

	const renderLinkContent = (item) => {
		let submenuIcon = item.items && (
			<i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
		);
		let badge = item.badge && (
			<span className="menuitem-badge">{item.badge}</span>
		);

		return (
			<React.Fragment>
				<i className={classNames("layout-menuitem-icon", item.icon)}></i>
				<span className="layout-menuitem-text">{t(item.label)}</span>
				{submenuIcon}
				{badge}
			</React.Fragment>
		);
	};

	const renderLink = (item, i) => {
		let content = renderLinkContent(item);

		if (item.to) {
			return (
				<NavLink
					to={item.to}
					end={!!item.end}
					onClick={(e) => onMenuItemClick(e, item, i)}
					role="menuitem"
					target={item.target}
					onMouseEnter={() => onMenuItemMouseEnter(i)}
					className={() =>
						classNames(item.styleClass, {
							"active-route": isMenuItemActive(item, location.pathname),
						})
					}
				>
					{content}
				</NavLink>
			);
		} else {
			return (
				<a
					href={item.url}
					tabIndex={item.url ? "" : 0}
					role="menuitem"
					onClick={(e) => onMenuItemClick(e, item, i)}
					target={item.target}
					onMouseEnter={() => onMenuItemMouseEnter(i)}
					onKeyDown={(e) => onKeyDown(e, item, i)}
					className={item.styleClass}
				>
					{content}
				</a>
			);
		}
	};

	const items =
		props.items &&
		props.items.map((item, i) => {
			let active = state.activeIndex === i;
			let styleClass = classNames(
				item.badgeStyleClass,
				{ "layout-root-menuitem": props.root },
				{ "active-menuitem": active }
			);
			let tooltip = props.root && (
				<div className="layout-menu-tooltip">
					<div className="layout-menu-tooltip-arrow"></div>
					<div className="layout-menu-tooltip-text">{t(item.label)}</div>
				</div>
			);
			const submenuRef = getSubmenuRef(i);

			return (
				<li className={styleClass} key={i} role="none">
					{item.items && props.root === true && <div className="arrow"></div>}
					{props.root && (
						<div>
							<span className="layout-menuitem-text">{t(item.label)}</span>
						</div>
					)}
					{renderLink(item, i)}
					{tooltip}
					{item.items ? (
						<CSSTransition
							nodeRef={submenuRef}
							classNames="layout-submenu"
							timeout={{ enter: 400, exit: 400 }}
							in={active}
						>
							<AppSubmenu
								ref={submenuRef}
								items={item.items}
								onMenuItemClick={props.onMenuItemClick}
								layoutMode={props.layoutMode}
								menuActive={props.menuActive}
							/>
						</CSSTransition>
					) : null}
				</li>
			);
		});

	return items ? (
		<ul ref={ref} role="menu" className={props.className}>
			{items}
		</ul>
	) : null;
});

AppSubmenu.displayName = "AppSubmenu";

AppSubmenu.defaultProps = {
	className: null,
	items: null,
	onMenuItemClick: null,
	onRootItemClick: null,
	root: false,
	layoutMode: null,
	menuActive: false,
};

AppSubmenu.propTypes = {
	className: PropTypes.string,
	items: PropTypes.array,
	onMenuItemClick: PropTypes.func,
	onRootItemClick: PropTypes.func,
	root: PropTypes.bool,
	layoutMode: PropTypes.string,
	menuActive: PropTypes.bool,
};

export class AppMenu extends Component {
	static defaultProps = {
		model: null,
		onMenuItemClick: null,
		onRootMenuItemClick: null,
		layoutMode: null,
		active: false,
	};

	static propTypes = {
		model: PropTypes.array,
		layoutMode: PropTypes.string,
		onMenuItemClick: PropTypes.func,
		onRootMenuItemClick: PropTypes.func,
		active: PropTypes.bool,
	};

	render() {
		return (
			<AppSubmenu
				items={this.props.model}
				className="layout-menu"
				menuActive={this.props.active}
				onRootItemClick={this.props.onRootMenuItemClick}
				onMenuItemClick={this.props.onMenuItemClick}
				root={true}
				layoutMode={this.props.layoutMode}
			/>
		);
	}
}
